import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldAlert, Database, MapPin, Mountain, BookOpen, Mail, Plus, X, List as ListIcon, LogOut, Trash2, Users, CalendarCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { API_URL } from '../config';

const AdminDashboard = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Data States
  const [stats, setStats] = useState({ sanctuaries: 0, treks: 0, stories: 0, messages: 0, users: 0, bookings: 0 });
  const [sanctuaries, setSanctuaries] = useState([]);
  const [treks, setTreks] = useState([]);
  const [stories, setStories] = useState([]);
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [bookings, setBookings] = useState([]);

  // Form States
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({});
  const [uploadingImage, setUploadingImage] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ show: false, type: null, id: null });

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      window.location.href = '/login';
      return;
    }

    // Verify token and fetch user info
    fetch(`${API_URL}/api/users/me`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
    .then(res => {
      if (!res.ok) throw new Error('Unauthorized');
      return res.json();
    })
    .then(data => {
      setUser(data);
      fetchAllData();
    })
    .catch(err => {
      console.error(err);
      localStorage.removeItem('token');
      window.location.href = '/login';
    })
    .finally(() => setLoading(false));
  }, []);

  const fetchAllData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      const [sanctuariesRes, treksRes, storiesRes, msgRes, usersRes, bookingsRes] = await Promise.all([
        fetch(`${API_URL}/api/sanctuaries`, { headers }),
        fetch(`${API_URL}/api/treks`, { headers }),
        fetch(`${API_URL}/api/admin/stories`, { headers }),
        fetch(`${API_URL}/api/contact`, { headers }),
        fetch(`${API_URL}/api/users`, { headers }),
        fetch(`${API_URL}/api/bookings`, { headers })
      ]);

      const [sancData, trekData, storyData, msgData, usersData, bookingsData] = await Promise.all([
        sanctuariesRes.json(), treksRes.json(), storiesRes.json(), msgRes.json(), usersRes.json(), bookingsRes.json()
      ]);

      setSanctuaries(sancData);
      setTreks(trekData);
      setStories(storyData);
      setMessages(msgData);
      setUsers(usersData);
      setBookings(bookingsData);

      setStats({
        sanctuaries: sancData.length || 0,
        treks: trekData.length || 0,
        stories: storyData.length || 0,
        messages: msgData.length || 0,
        users: usersData.length || 0,
        bookings: bookingsData.length || 0
      });
    } catch (e) {
      console.error("Failed to fetch data", e);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('is_admin');
    window.location.href = '/login';
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleArrayChange = (e, field) => {
    const val = e.target.value.split(',').map(s => s.trim());
    setFormData(prev => ({ ...prev, [field]: val }));
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    
    let endpoint = '';
    if (activeTab === 'sanctuaries') endpoint = '/api/sanctuaries';
    if (activeTab === 'treks') endpoint = '/api/treks';
    if (activeTab === 'stories') endpoint = '/api/stories';

    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      if (res.ok) {
        toast.success('Added successfully!');
        setShowAddForm(false);
        setFormData({});
        fetchAllData();
      } else {
        const err = await res.json();
        toast.error(`Error: ${JSON.stringify(err)}`);
      }
    } catch (error) {
      toast.error('Network Error');
    }
  };

  const handleImageUpload = (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingImage(true);
    
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData(prev => ({ ...prev, [field]: reader.result }));
      setUploadingImage(false);
    };
    reader.onerror = () => {
      toast.error("Failed to read file");
      setUploadingImage(false);
    };
    
    reader.readAsDataURL(file);
  };

  const handleDelete = (type, id) => {
    setDeleteModal({ show: true, type, id });
  };

  const executeDelete = async () => {
    const { type, id } = deleteModal;
    setDeleteModal({ show: false, type: null, id: null });

    const token = localStorage.getItem('token');
    
    // bookings need to map to /api/bookings/{id}
    let endpoint = `/api/${type}/${id}`;
    
    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Item deleted successfully.');
        fetchAllData();
      } else {
        toast.error('Failed to delete item.');
      }
    } catch (err) {
      console.error(err);
      toast.error('Network error while deleting.');
    }
  };

  const renderDeleteModal = () => {
    if (!deleteModal.show) return null;

    return (
      <AnimatePresence>
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
          onClick={() => setDeleteModal({ show: false, type: null, id: null })}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ type: 'spring', damping: 25, stiffness: 350 }}
            style={{
              backgroundColor: 'white',
              padding: '2rem',
              borderRadius: '16px',
              width: '100%',
              maxWidth: '440px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              border: '1px solid #f3f4f6',
              margin: '1.5rem',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
              <div 
                style={{ 
                  backgroundColor: '#fee2e2', 
                  borderRadius: '50%', 
                  padding: '0.75rem', 
                  color: '#dc2626',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                <ShieldAlert size={24} />
              </div>
              <div>
                <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.25rem', fontWeight: 'bold', color: '#111827' }}>
                  Delete Item
                </h3>
                <p style={{ margin: 0, fontSize: '0.95rem', color: '#4b5563', lineHeight: '1.5' }}>
                  Are you sure you want to delete this item? This action is permanent and cannot be undone.
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
              <button
                onClick={() => setDeleteModal({ show: false, type: null, id: null })}
                style={{
                  padding: '0.625rem 1.25rem',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#e5e7eb'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
              >
                Cancel
              </button>
              <button
                onClick={executeDelete}
                style={{
                  padding: '0.625rem 1.25rem',
                  backgroundColor: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#b91c1c'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#dc2626'}
              >
                Delete Item
              </button>
            </div>
          </motion.div>
        </div>
      </AnimatePresence>
    );
  };

  const handleApproveStory = async (id) => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/admin/stories/${id}/approve`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Story approved successfully.');
        fetchAllData();
      } else {
        toast.error('Failed to approve story.');
      }
    } catch (err) {
      console.error(err);
      toast.error('Network error while approving.');
    }
  };

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>Loading secure dashboard...</div>;
  }

  const renderTabs = () => {
    const tabs = [
      { id: 'overview', label: 'Overview', icon: <Database size={18}/> },
      { id: 'sanctuaries', label: 'Sanctuaries', icon: <MapPin size={18}/> },
      { id: 'treks', label: 'Trekking', icon: <Mountain size={18}/> },
      { id: 'stories', label: 'Stories', icon: <BookOpen size={18}/> },
      { id: 'bookings', label: 'Bookings', icon: <CalendarCheck size={18}/> },
      { id: 'messages', label: 'Messages', icon: <Mail size={18}/> },
      { id: 'users', label: 'Users', icon: <Users size={18}/> },
    ];

    return (
      <div style={{ display: 'flex', gap: '1rem', borderBottom: '2px solid #e5e7eb', marginBottom: '2rem', overflowX: 'auto', paddingBottom: '0.5rem' }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => { setActiveTab(tab.id); setShowAddForm(false); setFormData({}); }}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: activeTab === tab.id ? '#ea580c' : 'transparent',
              color: activeTab === tab.id ? 'white' : '#4b5563',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              transition: 'all 0.2s'
            }}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>
    );
  };

  const renderOverview = () => (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '2rem' }}>
      <motion.div whileHover={{ y: -5 }} style={{ backgroundColor: '#f0fdf4', padding: '2rem', borderRadius: '16px', border: '1px solid #bbf7d0' }}>
        <h3 style={{ fontSize: '1.3rem', margin: '0 0 1rem 0', color: '#166534', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><MapPin size={20}/> Sanctuaries</h3>
        <p style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#14532d', margin: '0' }}>{stats.sanctuaries}</p>
      </motion.div>
      <motion.div whileHover={{ y: -5 }} style={{ backgroundColor: '#fffbeb', padding: '2rem', borderRadius: '16px', border: '1px solid #fde68a' }}>
        <h3 style={{ fontSize: '1.3rem', margin: '0 0 1rem 0', color: '#92400e', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><Mountain size={20}/> Trekking</h3>
        <p style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#78350f', margin: '0' }}>{stats.treks}</p>
      </motion.div>
      <motion.div whileHover={{ y: -5 }} style={{ backgroundColor: '#fdf4ff', padding: '2rem', borderRadius: '16px', border: '1px solid #fbcfe8' }}>
        <h3 style={{ fontSize: '1.3rem', margin: '0 0 1rem 0', color: '#9d174d', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><BookOpen size={20}/> Stories</h3>
        <p style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#831843', margin: '0' }}>{stats.stories}</p>
      </motion.div>
      <motion.div whileHover={{ y: -5 }} style={{ backgroundColor: '#eff6ff', padding: '2rem', borderRadius: '16px', border: '1px solid #bfdbfe' }}>
        <h3 style={{ fontSize: '1.3rem', margin: '0 0 1rem 0', color: '#1e40af', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><Mail size={20}/> Messages</h3>
        <p style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#1e3a8a', margin: '0' }}>{stats.messages}</p>
      </motion.div>
      <motion.div whileHover={{ y: -5 }} style={{ backgroundColor: '#fef2f2', padding: '2rem', borderRadius: '16px', border: '1px solid #fecaca' }}>
        <h3 style={{ fontSize: '1.3rem', margin: '0 0 1rem 0', color: '#b91c1c', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><Users size={20}/> Users</h3>
        <p style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#7f1d1d', margin: '0' }}>{stats.users}</p>
      </motion.div>
    </div>
  );

  const renderTable = (headers, data, renderRow) => (
    <div style={{ overflowX: 'auto', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
        <thead>
          <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
            {headers.map((h, i) => <th key={i} style={{ padding: '1rem', color: '#374151', fontWeight: 600 }}>{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr><td colSpan={headers.length} style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>No records found.</td></tr>
          ) : (
            data.map((item, index) => (
              <tr key={index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                {renderRow(item)}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  const renderList = () => {
    if (activeTab === 'sanctuaries') {
      return renderTable(['Picture', 'Name', 'Location', 'Best Time', 'Actions'], sanctuaries, (item) => (
        <>
          <td style={{ padding: '0.75rem 1rem' }}>
            {item.image ? (
              <img 
                src={item.image} 
                alt={item.name} 
                style={{ 
                  width: '60px', 
                  height: '45px', 
                  borderRadius: '6px', 
                  objectFit: 'cover',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  border: '1px solid #e5e7eb'
                }} 
              />
            ) : (
              <div 
                style={{ 
                  width: '60px', 
                  height: '45px', 
                  borderRadius: '6px', 
                  backgroundColor: '#f3f4f6', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                  color: '#9ca3af',
                  border: '1px solid #e5e7eb'
                }}
              >
                No Pic
              </div>
            )}
          </td>
          <td style={{ padding: '1rem', fontWeight: 600 }}>{item.name}</td>
          <td style={{ padding: '1rem', color: '#4b5563' }}>{item.location}</td>
          <td style={{ padding: '1rem', color: '#4b5563' }}>{item.bestTimeToVisit}</td>
          <td style={{ padding: '1rem' }}>
            <button onClick={() => handleDelete('sanctuaries', item.id)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center' }} title="Delete">
              <Trash2 size={18} />
            </button>
          </td>
        </>
      ));
    }
    if (activeTab === 'treks') {
      return renderTable(['Picture', 'Name', 'Location', 'Difficulty', 'Actions'], treks, (item) => (
        <>
          <td style={{ padding: '0.75rem 1rem' }}>
            {item.image ? (
              <img 
                src={item.image} 
                alt={item.name} 
                style={{ 
                  width: '60px', 
                  height: '45px', 
                  borderRadius: '6px', 
                  objectFit: 'cover',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  border: '1px solid #e5e7eb'
                }} 
              />
            ) : (
              <div 
                style={{ 
                  width: '60px', 
                  height: '45px', 
                  borderRadius: '6px', 
                  backgroundColor: '#f3f4f6', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                  color: '#9ca3af',
                  border: '1px solid #e5e7eb'
                }}
              >
                No Pic
              </div>
            )}
          </td>
          <td style={{ padding: '1rem', fontWeight: 600 }}>{item.name}</td>
          <td style={{ padding: '1rem', color: '#4b5563' }}>{item.location}</td>
          <td style={{ padding: '1rem', color: item.difficultyColor }}>{item.difficulty}</td>
          <td style={{ padding: '1rem' }}>
            <button onClick={() => handleDelete('treks', item.id)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center' }} title="Delete">
              <Trash2 size={18} />
            </button>
          </td>
        </>
      ));
    }
    if (activeTab === 'stories') {
      return renderTable(['Picture', 'Title', 'Category', 'Author', 'Status', 'Actions'], stories, (item) => (
        <>
          <td style={{ padding: '0.75rem 1rem' }}>
            {item.image ? (
              <img 
                src={item.image} 
                alt={item.title} 
                style={{ 
                  width: '60px', 
                  height: '45px', 
                  borderRadius: '6px', 
                  objectFit: 'cover',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  border: '1px solid #e5e7eb'
                }} 
              />
            ) : (
              <div 
                style={{ 
                  width: '60px', 
                  height: '45px', 
                  borderRadius: '6px', 
                  backgroundColor: '#f3f4f6', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                  color: '#9ca3af',
                  border: '1px solid #e5e7eb'
                }}
              >
                No Pic
              </div>
            )}
          </td>
          <td style={{ padding: '1rem', fontWeight: 600 }}>{item.title}</td>
          <td style={{ padding: '1rem', color: '#4b5563' }}>{item.category}</td>
          <td style={{ padding: '1rem', color: '#4b5563' }}>{item.author}</td>
          <td style={{ padding: '1rem', color: item.status === 'Approved' ? '#ea580c' : '#b45309', fontWeight: 'bold' }}>{item.status}</td>
          <td style={{ padding: '1rem', display: 'flex', gap: '0.5rem' }}>
            {item.status === 'Pending' && (
              <button onClick={() => handleApproveStory(item.id)} style={{ padding: '0.4rem 0.8rem', backgroundColor: '#ea580c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 'bold' }}>
                Approve
              </button>
            )}
            <button onClick={() => handleDelete('stories', item.id)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center' }} title="Delete">
              <Trash2 size={18} />
            </button>
          </td>
        </>
      ));
    }
    if (activeTab === 'messages') {
      return renderTable(['Name', 'Email', 'Subject', 'Date', 'Actions'], messages, (item) => (
        <>
          <td style={{ padding: '1rem', fontWeight: 600 }}>{item.name}</td>
          <td style={{ padding: '1rem', color: '#4b5563' }}>{item.email}</td>
          <td style={{ padding: '1rem', color: '#4b5563' }}>{item.subject}</td>
          <td style={{ padding: '1rem', color: '#4b5563' }}>{new Date(item.created_at).toLocaleDateString()}</td>
          <td style={{ padding: '1rem' }}>
            <button onClick={() => handleDelete('contact', item.id)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center' }} title="Delete">
              <Trash2 size={18} />
            </button>
          </td>
        </>
      ));
    }
    if (activeTab === 'users') {
      return renderTable(['Email', 'Role', 'Actions'], users, (item) => (
        <>
          <td style={{ padding: '1rem', fontWeight: 600 }}>{item.email}</td>
          <td style={{ padding: '1rem', color: '#4b5563' }}>{item.is_admin ? 'Admin' : 'User'}</td>
          <td style={{ padding: '1rem' }}>
            <button onClick={() => handleDelete('users', item.id)} disabled={item.id === user.id} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: item.id === user.id ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center' }} title={item.id === user.id ? "Cannot delete yourself" : "Delete"}>
              <Trash2 size={18} opacity={item.id === user.id ? 0.3 : 1} />
            </button>
          </td>
        </>
      ));
    }
    if (activeTab === 'bookings') {
      return renderTable(['Type', 'Destination', 'Name', 'Phone', 'Date', 'Guests', 'Status', 'Actions'], bookings, (item) => (
        <>
          <td style={{ padding: '1rem', fontWeight: 600, color: item.booking_type === 'Trek' ? '#0284c7' : '#d97706' }}>{item.booking_type || 'Safari'}</td>
          <td style={{ padding: '1rem', fontWeight: 600 }}>{item.booking_type === 'Trek' ? item.trek_name : item.sanctuary_name}</td>
          <td style={{ padding: '1rem', color: '#4b5563' }}>{item.user_name}</td>
          <td style={{ padding: '1rem', color: '#4b5563' }}>{item.phone_number || '-'}</td>
          <td style={{ padding: '1rem', color: '#4b5563' }}>{item.date}</td>
          <td style={{ padding: '1rem', color: '#4b5563' }}>{item.guests}</td>
          <td style={{ padding: '1rem', color: '#ea580c', fontWeight: 'bold' }}>{item.status}</td>
          <td style={{ padding: '1rem' }}>
            <button onClick={() => handleDelete('bookings', item.id)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center' }} title="Delete">
              <Trash2 size={18} />
            </button>
          </td>
        </>
      ));
    }
  };

  const renderFormInput = (label, name, type = 'text', placeholder = '') => (
    <div style={{ marginBottom: '1.2rem' }}>
      <label style={{ display: 'block', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>{label}</label>
      {type === 'textarea' ? (
        <textarea required rows="4" name={name} onChange={handleInputChange} placeholder={placeholder} style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #d1d5db', outline: 'none' }}></textarea>
      ) : (
        <input required type={type} name={name} onChange={handleInputChange} placeholder={placeholder} style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #d1d5db', outline: 'none' }} />
      )}
    </div>
  );

  const renderFormArrayInput = (label, name, placeholder = 'Comma separated values') => (
    <div style={{ marginBottom: '1.2rem' }}>
      <label style={{ display: 'block', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>{label}</label>
      <input required type="text" onChange={(e) => handleArrayChange(e, name)} placeholder={placeholder} style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #d1d5db', outline: 'none' }} />
    </div>
  );

  const renderImageUpload = (label, name) => (
    <div style={{ marginBottom: '1.2rem' }}>
      <label style={{ display: 'block', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>{label}</label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        <input 
          type="file" 
          accept="image/*" 
          onChange={(e) => handleImageUpload(e, name)} 
          style={{ width: '100%', padding: '0.5rem', borderRadius: '8px', border: '1px solid #d1d5db', outline: 'none', backgroundColor: 'white' }} 
        />
        {uploadingImage && <span style={{ fontSize: '0.85rem', color: '#ea580c' }}>Uploading image...</span>}
        {formData[name] && (
          <div style={{ marginTop: '0.5rem' }}>
            <span style={{ fontSize: '0.85rem', color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>Preview:</span>
            <img src={formData[name]} alt="Preview" style={{ height: '100px', borderRadius: '8px', objectFit: 'cover' }} />
          </div>
        )}
      </div>
    </div>
  );

  const renderAddForm = () => {
    return (
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '16px', border: '1px solid #e5e7eb' }}>
        <h2 style={{ marginBottom: '1.5rem', color: '#111', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Plus /> Add New {activeTab === 'sanctuaries' ? 'Sanctuary' : 'Trek'}
        </h2>
        <form onSubmit={handleAddSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '0 2rem' }}>
            {activeTab === 'sanctuaries' && (
              <>
                {renderFormInput('Name', 'name')}
                {renderFormInput('Location', 'location')}
                {renderFormInput('Best Time to Visit', 'bestTimeToVisit')}
                {renderImageUpload('Upload Image', 'image')}
                {renderFormInput('Short Description', 'description', 'textarea')}
                {renderFormInput('Long Description', 'longDescription', 'textarea')}
                {renderFormArrayInput('Key Species (comma separated)', 'keySpecies')}
                {renderFormArrayInput('Highlights (comma separated)', 'highlights')}
              </>
            )}

            {activeTab === 'treks' && (
              <>
                {renderFormInput('Name', 'name')}
                {renderFormInput('Location', 'location')}
                {renderFormInput('Difficulty', 'difficulty')}
                {renderFormInput('Difficulty Color (Hex/Name)', 'difficultyColor', 'text', 'e.g. #ea580c or green')}
                {renderFormInput('Duration', 'duration')}
                {renderFormInput('Altitude', 'altitude')}
                {renderFormInput('Group Size', 'groupSize')}
                {renderFormInput('Best Time to Visit', 'bestTimeToVisit')}
                {renderImageUpload('Upload Image', 'image')}
                {renderFormInput('Short Description', 'description', 'textarea')}
                {renderFormInput('Long Description', 'longDescription', 'textarea')}
              </>
            )}


          </div>
          
          <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
            <button type="submit" style={{ padding: '0.75rem 2rem', backgroundColor: '#ea580c', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}>
              Save Entry
            </button>
            <button type="button" onClick={() => setShowAddForm(false)} style={{ padding: '0.75rem 2rem', backgroundColor: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </form>
      </motion.div>
    );
  };

  return (
    <div className="container section" style={{ minHeight: '80vh', backgroundColor: '#f9fafb', padding: '3rem 2rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '3rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <p style={{ margin: '0 0 0.25rem 0', fontSize: '0.9rem', fontWeight: 800, color: '#ea580c', letterSpacing: '0.08em', textTransform: 'uppercase', fontFamily: 'var(--font-sans)' }}>Adventure</p>
          <h1 className="section-title" style={{ marginBottom: '0.5rem' }}>Admin Dashboard</h1>
          <p className="section-subtitle">Welcome back, {user?.email}. Manage your platform here.</p>
        </div>
      </div>

      {renderTabs()}

      {activeTab === 'overview' && renderOverview()}

      {activeTab !== 'overview' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '1.5rem', color: '#111', textTransform: 'capitalize' }}>Manage {activeTab}</h2>
            {['sanctuaries', 'treks'].includes(activeTab) && !showAddForm && (
              <button 
                onClick={() => setShowAddForm(true)}
                style={{ padding: '0.5rem 1rem', backgroundColor: '#ea580c', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              >
                <Plus size={18} /> Add New
              </button>
            )}
          </div>

          {showAddForm ? renderAddForm() : renderList()}
        </>
      )}
      {renderDeleteModal()}
    </div>
  );
};

export default AdminDashboard;
